import { FaHeart, FaRegHeart } from 'react-icons/fa';
import { IoIosArrowDown } from 'react-icons/io';
import { memo, useState } from 'react';
import { useLikeHandler } from '@/hooks/useLikeHandler';
import useAuthBottomSheetStore from '@/stores/authBottomSheetStore';
import useSlaylistSelectorStore from '@/stores/SlaylistSelectorStore';
import type { Wishlist } from '@/types/Product.types';
import { isUserSigned } from '@/utils/getUserInfo';
import styles from './SaveButton.module.css';

interface SaveButtonProps {
  productId: number;
  initialWishlists: Wishlist[] | undefined;
}

const SaveButton = memo(({ productId, initialWishlists }: SaveButtonProps) => {
  const [animateHeart, setAnimateHeart] = useState(false);

  const openSheet = useAuthBottomSheetStore((state) => state.openSheet);
  const { openSlaylistSelector, selectedSlaylist } = useSlaylistSelectorStore();

  const productSlaylistOverride = useSlaylistSelectorStore(
    (state) => state.productSlaylistOverrides[productId]
  );

  let isLiked = false;
  let slaylistName: string | undefined | null = undefined;
  let primaryWishlist: Wishlist | undefined = undefined;

  if (productSlaylistOverride !== undefined) {
    // A change has happened in this session, so the override takes precedence.
    isLiked = productSlaylistOverride !== null;
    slaylistName = productSlaylistOverride
      ? productSlaylistOverride.name
      : null;
  } else if (initialWishlists && initialWishlists.length > 0) {
    // No override, so we use the initial data from the page load.
    isLiked = true;
    primaryWishlist =
      initialWishlists.find((w) => w.productList_Name !== 'Saved') ||
      initialWishlists[0];
    slaylistName = primaryWishlist?.productList_Name;
  }

  const { handleLikeClick } = useLikeHandler({
    initialLiked: isLiked,
    productId,
  });

  const handleSaveButtonClick = async (): Promise<void> => {
    const authResult = isUserSigned();

    const executeSaveAction = (): void => {
      if (isLiked) {
        // moving the item
        openSlaylistSelector({
          productId,
        });
      } else {
        setAnimateHeart(true);
        handleLikeClick();
        setTimeout(() => setAnimateHeart(false), 1000);
        if (!selectedSlaylist) openSlaylistSelector({ productId });
      }
    };

    if (authResult) {
      executeSaveAction();
    } else {
      openSheet(executeSaveAction);
    }
  };

  return (
    <>
      {animateHeart && (
        <div className={styles.animatedHeart}>
          <div className={styles.heartBorderWrapper}>
            <FaHeart className={styles.heazrtIcon} size={80} />
          </div>
        </div>
      )}
      <button
        className={styles.likeButton}
        aria-label={isLiked ? 'Change Slaylist' : 'Save to Slaylist'}
        onClick={handleSaveButtonClick}
      >
        {isLiked && slaylistName ? (
          <>
            <span>{slaylistName}</span>
            <IoIosArrowDown />
          </>
        ) : (
          <>
            <FaRegHeart color="#080928" />
            <span>Save</span>
          </>
        )}
      </button>
    </>
  );
});

SaveButton.displayName = 'SaveButton';

export default SaveButton;
